import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Sparkles, 
  Mail, 
  MessageSquare, 
  Share2, 
  Copy, 
  Check, 
  Clock, 
  RefreshCw,
  Wand2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AIMarketingTools = ({ businessId }) => {
  const [campaign, setCampaign] = useState(null);
  const [timing, setTiming] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  
  const [formData, setFormData] = useState({
    campaignType: 'kupon',
    discountPercent: 20,
    language: 'es'
  });

  const generateCampaign = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/ai-marketing/generate-campaign`,
        {
          campaign_type: formData.campaignType,
          offer_details: { discount_percent: formData.discountPercent },
          language: formData.language
        },
        { headers: { 'X-Business-Id': businessId } }
      );
      
      setCampaign(response.data.campaign);
      toast.success('¡Campaña generada con IA!');
      
      // Also get timing recommendation
      const timingRes = await axios.post(
        `${API}/ai-marketing/timing-recommendation`,
        { campaign_type: formData.campaignType, language: formData.language },
        { headers: { 'X-Business-Id': businessId } }
      );
      setTiming(timingRes.data.recommendation);
      
    } catch (error) {
      toast.error('Error generando campaña');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
    toast.success('¡Copiado al portapapeles!');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
      data-testid="ai-marketing-section"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-r from-violet-600 to-purple-600 rounded-xl flex items-center justify-center text-white">
          <Sparkles className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">Marketing con IA</h3>
          <p className="text-sm text-muted-foreground">
            Genera campañas completas con inteligencia artificial
          </p>
        </div>
      </div>

      {/* Configuration Form */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium mb-1">Tipo de Campaña</label>
          <select
            value={formData.campaignType}
            onChange={(e) => setFormData({ ...formData, campaignType: e.target.value })}
            className="w-full px-3 py-2 bg-muted rounded-lg border border-border focus:ring-2 focus:ring-violet-500"
            data-testid="campaign-type-select"
          >
            <option value="kupon">Promoción / Cupón</option>
            <option value="fidelizacion">Fidelización</option>
            <option value="nuevo_cliente">Captar Nuevos</option>
            <option value="reactivacion">Reactivación</option>
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">Descuento (%)</label>
          <input
            type="number"
            min="5"
            max="50"
            value={formData.discountPercent}
            onChange={(e) => setFormData({ ...formData, discountPercent: parseInt(e.target.value) })}
            className="w-full px-3 py-2 bg-muted rounded-lg border border-border focus:ring-2 focus:ring-violet-500"
            data-testid="discount-input"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-1">Idioma</label>
          <select
            value={formData.language}
            onChange={(e) => setFormData({ ...formData, language: e.target.value })}
            className="w-full px-3 py-2 bg-muted rounded-lg border border-border focus:ring-2 focus:ring-violet-500"
            data-testid="language-select"
          >
            <option value="es">Español</option>
            <option value="en">English</option>
            <option value="de">Deutsch</option>
            <option value="fr">Français</option>
          </select>
        </div>
      </div>

      {/* Generate Button */}
      <Button
        onClick={generateCampaign}
        disabled={loading}
        className="w-full mb-6 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
        data-testid="generate-campaign-btn"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Generando con Claude IA...
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <Wand2 className="w-4 h-4" />
            Generar Campaña con IA
          </span>
        )}
      </Button>

      {/* Results */}
      {campaign && (
        <div className="space-y-4">
          {/* Timing Recommendation */}
          {timing && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 rounded-xl p-4">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 mb-2">
                <Clock className="w-4 h-4" />
                <span className="font-medium text-sm">Mejor momento para publicar</span>
              </div>
              <p className="text-sm text-amber-800 dark:text-amber-300">
                <strong>Días:</strong> {timing.best_days?.join(', ')} | <strong>Horas:</strong> {timing.best_hours?.join(', ')}
              </p>
              <p className="text-xs text-amber-700/70 dark:text-amber-400/70 mt-1">
                {timing.reasoning}
              </p>
            </div>
          )}

          {/* SMS */}
          <CampaignCard
            icon={<MessageSquare className="w-4 h-4" />}
            title="SMS (160 caracteres)"
            content={campaign.short_text}
            onCopy={() => copyToClipboard(campaign.short_text, 'sms')}
            copied={copied === 'sms'}
            bgColor="bg-green-50 dark:bg-green-950/30"
            iconColor="text-green-600"
          />

          {/* Email Subject */}
          <CampaignCard
            icon={<Mail className="w-4 h-4" />}
            title="Asunto de Email"
            content={campaign.email_subject}
            onCopy={() => copyToClipboard(campaign.email_subject, 'subject')}
            copied={copied === 'subject'}
            bgColor="bg-blue-50 dark:bg-blue-950/30"
            iconColor="text-blue-600"
          />

          {/* Social Post */}
          <CampaignCard
            icon={<Share2 className="w-4 h-4" />}
            title="Post Redes Sociales"
            content={campaign.social_post}
            onCopy={() => copyToClipboard(campaign.social_post, 'social')}
            copied={copied === 'social'}
            bgColor="bg-pink-50 dark:bg-pink-950/30"
            iconColor="text-pink-600"
          />

          {/* Hashtags */}
          {campaign.hashtags && campaign.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {campaign.hashtags.map((tag, idx) => (
                <span
                  key={idx}
                  className="text-xs px-2 py-1 bg-muted rounded-full text-muted-foreground cursor-pointer hover:bg-violet-100 dark:hover:bg-violet-900/30"
                  onClick={() => copyToClipboard(`#${tag}`, `tag-${idx}`)}
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* AI Badge */}
          <div className="flex items-center justify-center gap-2 pt-2 text-xs text-muted-foreground">
            <Sparkles className="w-3 h-3" />
            {campaign.generated_by === 'claude-sonnet' 
              ? 'Generado con Claude Sonnet IA' 
              : 'Plantilla predefinida'}
          </div>
        </div>
      )}
    </motion.div>
  );
};

const CampaignCard = ({ icon, title, content, onCopy, copied, bgColor, iconColor }) => (
  <div className={`${bgColor} rounded-xl p-4`}>
    <div className="flex items-center justify-between mb-2">
      <div className={`flex items-center gap-2 ${iconColor}`}>
        {icon}
        <span className="font-medium text-sm">{title}</span>
      </div>
      <button
        onClick={onCopy}
        className="p-2 hover:bg-white/50 dark:hover:bg-black/20 rounded-lg transition-colors"
        data-testid={`copy-${title.toLowerCase().replace(/\s/g, '-')}`}
      >
        {copied ? (
          <Check className="w-4 h-4 text-green-600" />
        ) : (
          <Copy className="w-4 h-4 text-gray-500" />
        )}
      </button>
    </div>
    <p className="text-sm whitespace-pre-wrap">{content}</p>
  </div>
);

export default AIMarketingTools;
